import {
  useCallback,
  useLayoutEffect,
  useState,
  type MutableRefObject,
} from 'react';

// Reports whether the ref'd element is vertically scrollable. Callback
// ref so conditionally-mounted elements (e.g. inside AccordionContent)
// are picked up. Observes direct children too — a child growing (Radix
// accordion opening) changes scrollHeight, and the child's bounds
// genuinely change so ResizeObserver fires. `externalRef`, if provided,
// mirrors the node for callers that need a RefObject (e.g. the
// InfiniteScrollSentinel's IntersectionObserver root).
export function useIsScrollable<T extends HTMLElement>(
  externalRef?: MutableRefObject<T | null>,
): [(el: T | null) => void, boolean] {
  const [element, setElement] = useState<T | null>(null);
  const [isScrollable, setIsScrollable] = useState(false);

  const refCallback = useCallback(
    (el: T | null) => {
      if (externalRef) externalRef.current = el;
      setElement(el);
    },
    [externalRef],
  );

  useLayoutEffect(() => {
    if (!element) {
      setIsScrollable(false);
      return;
    }
    const check = () => {
      setIsScrollable(element.scrollHeight > element.clientHeight);
    };
    const ro = new ResizeObserver(check);
    const observeAll = () => {
      ro.disconnect();
      ro.observe(element);
      for (const child of Array.from(element.children)) ro.observe(child);
    };
    observeAll();
    check();
    // childList only — style/attribute toggles (e.g. Radix accordion
    // animating height) are caught by the ResizeObserver on children.
    const mo = new MutationObserver(() => {
      observeAll();
      check();
    });
    mo.observe(element, { childList: true });
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [element]);

  return [refCallback, isScrollable];
}
